import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { environment } from '../../../environment/environment';
import { finalize, timeout } from 'rxjs';

export interface UserSyncErrorsPayload {
  errors: UserSyncErrorRow[];
  truncated: boolean;
  has_stored_details: boolean;
}

export interface OrgSyncUserErrorsDialogData {
  title: string;
  runId: number;
  usersErrors: number;
  /** From overview — avoids extra API round-trip */
  preloaded?: UserSyncErrorsPayload | null;
}

export interface UserSyncErrorRow {
  user_index: number;
  email: string | null;
  name: string | null;
  reason: string;
  code: string;
}

export interface UserSyncErrorsResponse {
  run_id: number;
  users_errors: number;
  errors: UserSyncErrorRow[];
  truncated: boolean;
  has_stored_details: boolean;
}

@Component({
  selector: 'app-org-sync-user-errors-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <h2 mat-dialog-title>Brugerfejl ved sync</h2>
    <mat-dialog-content class="user-errors-content">
      <p class="subtitle">{{ data.title }}</p>
      <p class="muted">Kørsel #{{ data.runId }} · registrerede fejl: {{ data.usersErrors }}</p>

      @if (loading) {
        <div class="loading-wrap">
          <mat-spinner diameter="36"></mat-spinner>
        </div>
      } @else if (loadError) {
        <p class="error-text">{{ loadError }}</p>
      } @else if (!response?.has_stored_details && (data.usersErrors ?? 0) > 0) {
        <p class="warn-text">
          Denne kørsel har {{ data.usersErrors }} fejl, men ingen detaljer blev gemt (typisk ældre kørsel før
          opdatering). Kør sync igen for at se brugerliste her.
        </p>
      } @else if ((response?.errors?.length ?? 0) === 0) {
        <p class="muted">Ingen brugerfejl registreret for denne kørsel.</p>
      } @else {
        @if (response?.truncated) {
          <p class="warn-text">
            Listen er afkortet (maks. antal gemte fejl). Se også RetrieveOrganisationUsers.log på serveren.
          </p>
        }
        <table mat-table [dataSource]="response!.errors" class="errors-table">
          <ng-container matColumnDef="user_index">
            <th mat-header-cell *matHeaderCellDef>#</th>
            <td mat-cell *matCellDef="let row">{{ row.user_index }}</td>
          </ng-container>
          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef>E-mail</th>
            <td mat-cell *matCellDef="let row">{{ row.email ?? '–' }}</td>
          </ng-container>
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Navn</th>
            <td mat-cell *matCellDef="let row">{{ row.name ?? '–' }}</td>
          </ng-container>
          <ng-container matColumnDef="reason">
            <th mat-header-cell *matHeaderCellDef>Årsag</th>
            <td mat-cell *matCellDef="let row" class="reason-cell">{{ row.reason }}</td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Luk</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .user-errors-content {
        min-width: min(720px, 90vw);
        max-width: 960px;
      }
      .subtitle {
        margin: 0 0 4px;
        font-weight: 500;
      }
      .muted {
        color: rgba(0, 0, 0, 0.6);
        font-size: 13px;
      }
      .warn-text {
        color: #e65100;
        font-size: 14px;
      }
      .error-text {
        color: #b71c1c;
      }
      .loading-wrap {
        display: flex;
        justify-content: center;
        padding: 24px;
      }
      .errors-table {
        width: 100%;
        margin-top: 12px;
      }
      .reason-cell {
        white-space: normal;
        line-height: 1.35;
        max-width: 420px;
      }
    `,
  ],
})
export class OrgSyncUserErrorsDialogComponent implements OnInit {
  loading = true;
  loadError: string | null = null;
  response: UserSyncErrorsResponse | null = null;
  displayedColumns = ['user_index', 'email', 'name', 'reason'];

  constructor(
    public dialogRef: MatDialogRef<OrgSyncUserErrorsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrgSyncUserErrorsDialogData,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const pre = this.data.preloaded;
    if (pre !== undefined && pre !== null) {
      this.applyResponse({
        run_id: this.data.runId,
        users_errors: this.data.usersErrors,
        errors: pre.errors,
        truncated: pre.truncated,
        has_stored_details: pre.has_stored_details,
      });
      return;
    }

    this.http
      .get<UserSyncErrorsResponse>(
        `${environment.apiUrl}/organizations/identity-sync/run-user-errors`,
        { params: { run_id: String(this.data.runId) } }
      )
      .pipe(
        timeout(25000),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (body) => this.applyResponse(body),
        error: (err) => {
          const msg =
            (err as { error?: { message?: string }; name?: string })?.error
              ?.message ??
            ((err as { name?: string }).name === 'TimeoutError'
              ? 'Hentning af brugerfejl tog for lang tid (tjek at API-endpoint er deployet).'
              : 'Kunne ikke hente brugerfejl.');
          this.loadError = msg;
        },
      });
  }

  private applyResponse(body: UserSyncErrorsResponse): void {
    this.loading = false;
    this.response = body;
    this.cdr.markForCheck();
  }
}
